export type BibleEditorialNoteKind = "translator-note" | "subscription";

export interface BibleTextSegment {
  kind: "text" | "supplied";
  text: string;
}

export interface BibleEditorialNote {
  kind: BibleEditorialNoteKind;
  text: string;
}

export interface ParsedBibleVerse {
  segments: BibleTextSegment[];
  notes: BibleEditorialNote[];
  readableText: string;
}

const NOTE_PATTERNS = [
  /:/u,
  /\b(?:Heb|Gr|Chal)\.\s/iu,
  /^(?:many ancient copies|this verse is not found)\b/iu,
  /^(?:written\b|it was written\b|the first (?:epistle|to)\b|the second epistle\b)/iu,
  /^but\)\s/iu,
  /\.\.\.;\s*or\b/iu,
  /;\s*or,\s*/iu,
] as const;

function normalizeSpacing(value: string): string {
  return value
    .replace(/\s+([,.;!?])/gu, "$1")
    .replace(/\s+/gu, " ")
    .trim();
}

function isEditorialNote(value: string): boolean {
  const normalized = value.trim();
  return NOTE_PATTERNS.some((pattern) => pattern.test(normalized));
}

function appendSegment(segments: BibleTextSegment[], segment: BibleTextSegment) {
  if (!segment.text) return;
  const previous = segments.at(-1);
  if (previous?.kind === segment.kind) {
    previous.text += segment.text;
    return;
  }
  segments.push(segment);
}

/**
 * The bundled KJV source uses braces for two unrelated print conventions:
 * translator-supplied words and marginal/editorial notes. This parser preserves
 * the supplied words as Scripture typography while separating notes from the
 * readable verse text.
 */
export function parseKjvEditorialVerse(rawText: string): ParsedBibleVerse {
  const notes: BibleEditorialNote[] = [];
  const withoutSubscriptions = rawText.replace(/\s*«([\s\S]*?)»/gu, (_match, subscription: string) => {
    const text = normalizeSpacing(subscription.replace(/[{}]/gu, ""));
    if (text) notes.push({ kind: "subscription", text });
    return "";
  });
  const segments: BibleTextSegment[] = [];
  let cursor = 0;
  let depth = 0;
  let blockStart = -1;
  let lastBlockWasNote = false;

  for (let index = 0; index < withoutSubscriptions.length; index += 1) {
    const character = withoutSubscriptions[index];
    if (character === "{") {
      if (depth === 0) {
        appendSegment(segments, {
          kind: "text",
          text: withoutSubscriptions.slice(cursor, index),
        });
        blockStart = index + 1;
      }
      depth += 1;
      continue;
    }
    if (character !== "}") continue;
    if (depth === 0) {
      const trailingText = withoutSubscriptions.slice(cursor, index);
      if (lastBlockWasNote && notes.length > 0) {
        const previousNote = notes.at(-1)!;
        previousNote.text = normalizeSpacing(`${previousNote.text} ${trailingText}`);
      } else {
        appendSegment(segments, { kind: "text", text: trailingText });
      }
      cursor = index + 1;
      continue;
    }
    depth -= 1;
    if (depth !== 0) continue;

    const value = withoutSubscriptions
      .slice(blockStart, index)
      .replace(/[{}]/gu, "");
    if (isEditorialNote(value)) {
      notes.push({ kind: "translator-note", text: normalizeSpacing(value) });
      lastBlockWasNote = true;
    } else {
      appendSegment(segments, { kind: "supplied", text: value });
      lastBlockWasNote = false;
    }
    cursor = index + 1;
    blockStart = -1;
  }

  if (depth > 0 && blockStart >= 0) {
    const value = withoutSubscriptions
      .slice(blockStart)
      .replace(/[{}]/gu, "");
    if (isEditorialNote(value)) {
      notes.push({ kind: "translator-note", text: normalizeSpacing(value) });
    } else {
      appendSegment(segments, { kind: "supplied", text: value });
    }
  } else {
    appendSegment(segments, {
      kind: "text",
      text: withoutSubscriptions.slice(cursor),
    });
  }

  const readableText = normalizeSpacing(segments.map((segment) => segment.text).join(""));
  return {
    segments,
    notes,
    readableText,
  };
}

export function readableBibleText(text: string, versionId?: string): string {
  return versionId === "kjv" ? parseKjvEditorialVerse(text).readableText : normalizeSpacing(text);
}
